import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  LockOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  // CheckCircleOutlined,
  DownOutlined,
  UpOutlined,
  TableOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { usePermissions, PERMISSIONS } from "../../context/PermissionContext";

interface SidebarMenuProps {
  isExpanded: boolean;
  isMobile: boolean;
}

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  path?: string; // Optional for parent menus
  requiredPermissions?: number[]; // Array of permission IDs required to see this item
  requireAll?: boolean; // If true, all permissions are required; otherwise, any one is sufficient
  subMenu?: MenuItem[]; // Submenu items
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ isExpanded, isMobile }) => {
  // Track open/closed state for each dropdown menu
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  // Get permissions from context
  const { hasAnyPermission, hasAllPermissions } = usePermissions();

  const toggleMenu = (menuLabel: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuLabel]: !prev[menuLabel],
    }));
  };

  const menuItems: MenuItem[] = [
    {
      label: "Dashboard",
      icon: <DashboardOutlined />,
      path: "/dashboard",
      requiredPermissions: [PERMISSIONS.ACCESS_DASHBOARD],
    },
    {
      label: "Table Management",
      icon: <TableOutlined />,
      path: "/table-setup",
      requiredPermissions: [PERMISSIONS.VIEW_TABLES],
    },
    {
      label: "Location Management",
      icon: <EnvironmentOutlined />,
      path: "/locations",
      requiredPermissions: [PERMISSIONS.VIEW_LOCATIONS],
    },
    {
      label: "Orders",
      icon: <ShoppingCartOutlined />,
      path: "/orders",
      requiredPermissions: [PERMISSIONS.ACCESS_ORDERS],
    },
    {
      label: "User Management",
      icon: <UserOutlined />,
      path: "/user-management",
      requiredPermissions: [PERMISSIONS.VIEW_USERS],
    },
    {
      label: "Roles & Permissions",
      icon: <LockOutlined />,
      path: "/roles-permission",
      requiredPermissions: [PERMISSIONS.VIEW_ROLES],
    },
    {
      label: "Inventory Management",
      icon: <AppstoreOutlined />,
      requiredPermissions: [PERMISSIONS.VIEW_INVENTORY],
      subMenu: [
        {
          label: "Inventories",
          icon: <AppstoreOutlined />,
          path: "/inventory",
          requiredPermissions: [PERMISSIONS.VIEW_INVENTORY],
        },
        {
          label: "Vendors",
          icon: <UserOutlined />,
          path: "/vendors",
          requiredPermissions: [PERMISSIONS.VIEW_VENDORS],
        },
      ],
    },
    {
      label: "Staff Schedule",
      icon: <CalendarOutlined />,
      path: "/staff-scheduling",
      requiredPermissions: [PERMISSIONS.VIEW_SCHEDULES],
    },
    // {
    //   label: "Stock Availability",
    //   icon: <CheckCircleOutlined />,
    //   path: "/stock-availability",
    //   requiredPermissions: [PERMISSIONS.VIEW_INVENTORY],
    // },
  ];

  // Check if a menu item should be visible based on permissions
  const hasMenuPermission = (item: MenuItem): boolean => {
    if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
      return true; // No permissions required
    }

    if (item.requireAll) {
      return hasAllPermissions(item.requiredPermissions);
    } else {
      return hasAnyPermission(item.requiredPermissions);
    }
  };

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems.filter((item) => {
    // If item has a submenu, check if any subitem is accessible
    if (item.subMenu) {
      const accessibleSubItems = item.subMenu.filter(hasMenuPermission);
      return accessibleSubItems.length > 0 && hasMenuPermission(item);
    }
    // Otherwise just check the item's permission
    return hasMenuPermission(item);
  });

  return (
    <ul className="py-4">
      {filteredMenuItems.map((item, index) => (
        <li key={index} className="mb-2">
          {item.subMenu ? (
            <>
              <div
                onClick={() => toggleMenu(item.label)}
                className="flex items-center w-[90%] mx-auto px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                <span className="text-lg">{item.icon}</span>
                {(isMobile || isExpanded) && (
                  <>
                    <span className="text-sm ml-4">{item.label}</span>
                    <span className="ml-auto">
                      {openMenus[item.label] ? (
                        <UpOutlined />
                      ) : (
                        <DownOutlined />
                      )}
                    </span>
                  </>
                )}
              </div>
              {openMenus[item.label] && (isMobile || isExpanded) && (
                <ul className="ml-6 mt-1">
                  {item.subMenu
                    .filter(hasMenuPermission)
                    .map((subItem, subIndex) => (
                      <li key={subIndex} className="mb-1">
                        <NavLink
                          to={subItem.path!}
                          className={({ isActive }) =>
                            `flex items-center w-[85%] mx-auto px-4 py-2 rounded-lg ${
                              isActive
                                ? "bg-primary text-white hover:bg-primaryHover"
                                : "text-gray-700 hover:bg-gray-100"
                            }`
                          }
                        >
                          <span className="text-lg">{subItem.icon}</span>
                          <span className="text-sm ml-3">{subItem.label}</span>
                        </NavLink>
                      </li>
                    ))}
                </ul>
              )}
            </>
          ) : (
            <NavLink
              to={item.path!}
              className={({ isActive }) =>
                `flex items-center w-[90%] mx-auto px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-primary text-white hover:bg-primaryHover"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              {(isMobile || isExpanded) && (
                <span className="text-sm ml-4">{item.label}</span>
              )}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );
};

export default SidebarMenu;
